/**
 * The composer.
 *
 * Geometry measured from T3's live DOM rather than approximated:
 *
 * - A floating `rounded-3xl` card (22px) with the outline drawn as an
 *   `::after` hairline, not a border. A real border would change the box model
 *   and the composer would jump 2px the moment focus moved.
 * - `px-4 pt-4 pb-2` inside, so the text sits above the control row.
 * - Controls are `rounded-full` pills, 32px tall, so the row is dense but every
 *   target still clears the 28px minimum on touch.
 * - The send button is a 32px circle, blue when there is text, gray when not.
 *
 * Three iOS behaviours are handled here, each of which was a real bug:
 *
 * 1. **The field is never unmounted or keyed.** The previous client recreated
 *    the textarea as part of a wholesale re-render, which dropped the caret and
 *    closed the keyboard mid-sentence. React keeps the node identity.
 * 2. **Send is optimistic.** The text clears and the button shows a pending
 *    state *before* the round trip, because the round trip is a websocket call to
 *    a device that may be busy — a send that only reacted on the reply read as a
 *    dead button.
 * 3. **16px font minimum**, or iOS zooms the viewport on focus.
 */

import { useEffect, useRef, useState } from "react";
import { useAppState, useStore } from "./store-context";
import { IconArrowUp, IconStop } from "./icons";
import type { SandboxLevel } from "../lib/models";
import { BranchToolbar } from "./branch-toolbar";

export function Composer() {
  const state = useAppState();
  const store = useStore();
  const textarea = useRef<HTMLTextAreaElement>(null);

  // Grow with the content, capped, and reset when the draft clears. Height is
  // set imperatively because a CSS-only auto-grow needs a hidden mirror element
  // and this is two lines.
  useEffect(() => {
    const node = textarea.current;
    if (node === null) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 208)}px`;
  }, [state.draft.text]);

  const hasText = state.draft.text.trim().length > 0;
  const busy = state.sending;
  const working = state.openChatId !== undefined && store.statusFor(state.openChatId) === "working";
  const chat = state.chats.find((candidate) => candidate.id === state.openChatId);
  const space = state.spaces.find((candidate) => candidate.id === chat?.spaceId);

  const submit = (): void => {
    if (!hasText || busy) return;
    void store.send();
  };

  return (
    <div className="sticky bottom-0 z-10 bg-gradient-to-t from-background via-background to-transparent pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <form
        data-chat-composer-overlay="true"
        className="chat-composer-lane mx-auto w-full max-w-[var(--chat-content-max-width)]"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div data-slot="composer-shell" className="@container/composer-surface group/composer-surface relative isolate mx-auto w-full max-w-(--chat-content-max-width) before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-3xl before:bg-card/80 before:backdrop-blur-xl">
          <div data-slot="composer-host" className="relative z-10 w-full rounded-3xl shadow-composer after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[var(--chat-composer-outline)]">
            <div data-chat-composer-main-surface="true" className="group relative z-10 rounded-3xl p-px">
              <div className="rounded-3xl transition-[background-color] duration-200">
                <div data-chat-composer-body="true" className="relative px-3 pb-2 pt-3.5 sm:px-4 sm:pb-2 sm:pt-4">
            <textarea
              ref={textarea}
              value={state.draft.text}
              onChange={(event) => store.setDraft({ text: event.target.value })}
              onKeyDown={(event) => {
                // Enter sends on a hardware keyboard; Shift+Enter and the iPad's
                // software Return both insert a newline, which is what a touch
                // keyboard expects.
                if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  submit();
                }
              }}
              rows={1}
              placeholder="Ask anything, @tag files/folders, $use skills, or / for commands"
              aria-label="Message"
              enterKeyHint="send"
              className="block max-h-52 min-h-[1.5rem] w-full resize-none bg-transparent text-base leading-relaxed text-foreground outline-none placeholder:text-placeholder"
            />
            <div className="mt-2 flex items-center gap-1.5">
              <ModelPill />
              <SandboxPill />
              <div className="flex-1" />
              {working ? (
                <button
                  type="button"
                  onClick={() => void store.interrupt()}
                  title="Stop"
                  aria-label="Stop"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"
                >
                  <IconStop className="size-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!hasText || busy}
                  title="Send"
                  aria-label="Send"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:bg-muted disabled:text-muted-foreground"
                >
                  <IconArrowUp className="size-4" />
                </button>
              )}
            </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {space !== undefined ? (
          <BranchToolbar
            space={space}
            spaces={state.spaces}
            devices={state.devices}
            refs={[]}
            worktree={chat?.checkoutId !== null && chat?.checkoutId !== undefined}
            baseRef={chat?.branch ?? undefined}
            disabled
          />
        ) : null}
        {state.error !== undefined ? (
          <p className="px-1 pt-1.5 text-xs text-error-foreground">{state.error}</p>
        ) : null}
      </form>
    </div>
  );
}

/** Model and reasoning, as one pill. Reads the open chat's config as its base so
 * the pill shows what the *next* turn will actually use. */
function ModelPill() {
  const state = useAppState();
  const store = useStore();
  const [open, setOpen] = useState(false);
  const chat = state.chats.find((candidate) => candidate.id === state.openChatId);
  const harness = state.draft.harness ?? chat?.config?.harness;
  const model = state.draft.model ?? chat?.config?.model ?? null;
  const label = model ?? harness ?? "Model";

  useEffect(() => {
    if (open && harness !== undefined) void store.loadModels(harness);
  }, [open, harness, store]);

  return (
    <div className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex h-8 max-w-[11rem] items-center gap-1 rounded-full border border-border px-2.5 text-xs text-muted-foreground hover:bg-accent"
      >
        <span className="truncate">{label}</span>
      </button>
      {open ? (
        <div className="absolute bottom-10 left-0 z-20 max-h-72 w-60 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-lg">
          {state.models.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">No models listed.</p>
          ) : (
            state.models.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => {
                  store.setDraft({ model: entry.id });
                  setOpen(false);
                }}
                className={`flex w-full flex-col items-start gap-0.5 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent ${
                  entry.id === model ? "bg-accent" : ""
                }`}
              >
                <span className="w-full truncate text-foreground">{entry.label ?? entry.id}</span>
                {entry.description !== undefined ? (
                  <span className="w-full truncate text-2xs text-muted-foreground">
                    {entry.description}
                  </span>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

const SANDBOX_LABELS: Record<SandboxLevel, string> = {
  readOnly: "Read only",
  workspaceWrite: "Can edit",
  dangerFullAccess: "Full access"
};

/** Permissions, as T3's second pill. The levels are the engine's own three, not
 * a friendlier re-invention — the label has to mean what the engine will do. */
function SandboxPill() {
  const state = useAppState();
  const store = useStore();
  const [open, setOpen] = useState(false);
  const chat = state.chats.find((candidate) => candidate.id === state.openChatId);
  const current = (state.draft.sandbox ?? chat?.config?.sandbox ?? "workspaceWrite") as SandboxLevel;
  return (
    <div className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex h-8 items-center rounded-full border border-border px-2.5 text-xs text-muted-foreground hover:bg-accent"
      >
        {SANDBOX_LABELS[current] ?? current}
      </button>
      {open ? (
        <div className="absolute bottom-10 left-0 z-20 w-52 rounded-xl border border-border bg-popover p-1 shadow-lg">
          {(Object.keys(SANDBOX_LABELS) as SandboxLevel[]).map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => {
                store.setDraft({ sandbox: level });
                setOpen(false);
              }}
              className={`flex w-full flex-col items-start rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent ${
                level === current ? "bg-accent" : ""
              }`}
            >
              <span className="text-foreground">{SANDBOX_LABELS[level]}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
